import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { map, catchError } from 'rxjs/operators';

// Interfaces
export interface LoginResponse {
  jwt: string;
}

export interface RegisterRequest {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  contractId: number;
  phoneNumber: string;
  schoolName?: string;
  city?: string;
  address: string;
  code: string;
  extraPhoneNumber?: string;
}

export interface RegisterResponse {
  user: {
    id: number;
    firstName: string;
    lastName: string;
    createdAt: string;
  };
}

export interface EmailExistsResponse {
  exists: boolean;
}

export interface SubjectProblemsResponse {
  subjectName: string;
  problemIds: { id: number }[];
}

export interface ProblemSubject {
  hierarchy: string;
  kind?: string;
  test?: boolean;
}

export interface ProblemDifficulty {
  id: number;
}

export interface Problem {
  id: number;
  question: string;
  correct: string;
  wrong2: string;
  wrong3: string;
  wrong4: string;
  difficulty: ProblemDifficulty;
  subject: ProblemSubject;
}

export interface ProblemsByIdsResponse {
  problems: Problem[];
}

export interface ProblemDetail {
  id: number;
  clue: string;
  solution: string;
  article: string;
  subject: {
    hierarchy: string;
  };
}

export interface ProblemDetailResponse {
  problem: ProblemDetail;
}

@Injectable({
  providedIn: 'root'
})
export class ApiService {
  private readonly API_BASE = '/api';

  constructor(private http: HttpClient) {}

  // Helper to get auth headers
  private getAuthHeaders(): HttpHeaders {
    const token = localStorage.getItem('token');
    let headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    if (token) {
      headers = headers.set('Authorization', `Bearer ${token}`);
    }
    return headers;
  }

  // Error handler
  private handleError(error: HttpErrorResponse): Observable<never> {
    if (error.status === 401) {
      localStorage.removeItem('token');
      window.location.assign('/#/login');
    }
    return throwError(() => error);
  }

  // ========== AUTH ==========

  login(email: string, password: string): Observable<LoginResponse> {
    return this.http.post<LoginResponse>(
      `${this.API_BASE}/auth/login`,
      { email, password },
      { headers: new HttpHeaders({ 'Content-Type': 'application/json' }) }
    ).pipe(catchError(this.handleError.bind(this)));
  }

  register(data: RegisterRequest): Observable<RegisterResponse> {
    return this.http.post<RegisterResponse>(
      `${this.API_BASE}/auth/register`,
      data,
      { headers: new HttpHeaders({ 'Content-Type': 'application/json' }) }
    ).pipe(catchError(this.handleError.bind(this)));
  }

  isEmailExists(email: string): Observable<boolean> {
    return this.http.get<EmailExistsResponse>(
      `${this.API_BASE}/users/email-exists`,
      { 
        params: { email },
        headers: new HttpHeaders({ 'Content-Type': 'application/json' })
      }
    ).pipe(
      map(response => response.exists),
      catchError(this.handleError.bind(this))
    );
  }

  // ========== PROBLEMS ==========

  getSubjectProblems(hierarchy: string): Observable<{ id: number }[]> {
    return this.http.get<SubjectProblemsResponse>(
      `${this.API_BASE}/subjects/${encodeURIComponent(hierarchy)}/problems`,
      { headers: this.getAuthHeaders() }
    ).pipe(
      map(response => response.problemIds),
      catchError(this.handleError.bind(this))
    );
  }

  getProblemsByIds(ids: number[], inTest: boolean = false): Observable<Problem[]> {
    return this.http.post<ProblemsByIdsResponse>(
      `${this.API_BASE}/problems/by-ids`,
      { ids, inTest },
      { headers: this.getAuthHeaders() }
    ).pipe(
      map(response => response.problems),
      catchError(this.handleError.bind(this))
    );
  }

  getProblemDetail(id: number): Observable<ProblemDetail> {
    return this.http.get<ProblemDetailResponse>(
      `${this.API_BASE}/problems/${id}`,
      { headers: this.getAuthHeaders() }
    ).pipe(
      map(response => response.problem),
      catchError(this.handleError.bind(this))
    );
  }
}
